import React, { useState, useEffect, useRef } from 'react';
import { Video, Upload, Quote, CheckSquare, Square, Loader2, Globe, Building, Mic, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { SlidePanel } from '@/components/ui/slide-panel';
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from '@/contexts/AuthContext';
import { VideoService } from '@/services/videoService';
import { knowledgeService } from '@/services/knowledgeService';
import { apiClient } from '@/lib/apiClient';
import { FAQItem } from '@/types/faq';

interface SalesTrainingManagerProps {
  onUpdate?: () => void;
}

interface VideoWithKnowledge extends FAQItem {
  hasTranscript?: boolean;
  isInKnowledgeBase?: boolean;
  isGlobal?: boolean;
}

export const SalesTrainingManager: React.FC<SalesTrainingManagerProps> = ({ onUpdate }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [videos, setVideos] = useState<VideoWithKnowledge[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [transcribing, setTranscribing] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedVideoForUpload, setSelectedVideoForUpload] = useState<VideoWithKnowledge | null>(null);
  const [manualTranscript, setManualTranscript] = useState('');
  const [uploadingTranscript, setUploadingTranscript] = useState(false);
  
  // Use Solerro tenant ID for global shared content
  const SHARED_TENANT_ID = '73ef7154-be00-4600-a4a6-34185ea3e6b4';

  useEffect(() => {
    let mounted = true;
    
    const load = async () => {
      if (user && mounted) {
        await loadVideos();
      }
    };
    
    load();
    
    return () => {
      mounted = false;
    };
  }, [user]);

  const loadVideos = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Prevent concurrent loading
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      // Load videos via API client
      
      // Get all videos from the videos table
      const allVideos = await VideoService.getVideos();
      
      // Get knowledge documents to check which videos have transcripts
      // For now, just check the shared tenant since all videos are there
      const sharedDocs = await knowledgeService.getDocuments(SHARED_TENANT_ID, 'sales_training');
      const knowledgeDocs = sharedDocs;
      const videoIdsWithTranscripts = new Set(
        knowledgeDocs
          .filter(doc => doc.metadata?.video_id)
          .map(doc => doc.metadata.video_id)
      );

      // Enhance videos with knowledge base status
      const enhancedVideos: VideoWithKnowledge[] = await Promise.all(
        allVideos.map(async (video) => {
          // For now, assume all videos are global since we're using API
          // The backend should handle tenant isolation
          const isGlobal = true; // Backend will handle proper scoping
          const hasTranscript = videoIdsWithTranscripts.has(video.id);

          return {
            ...video,
            hasTranscript,
            isInKnowledgeBase: hasTranscript,
            isGlobal
          };
        })
      );

      setVideos(enhancedVideos);
      
      // Set initially selected videos (those already in knowledge base)
      const initialSelected = new Set(
        enhancedVideos
          .filter(v => v.isInKnowledgeBase)
          .map(v => v.id)
      );
      setSelectedVideos(initialSelected);

    } catch (error) {
      console.error('Error loading videos - Full error:', error);
      toast({
        title: "Error",
        description: `Failed to load training videos: ${(error as Error).message || 'Unknown error'}`,
        variant: "destructive"
      });
      // Don't set videos to empty on error - keep any existing data
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleTranscribeVideo = async (video: VideoWithKnowledge) => {
    if (!user?.id) return;

    setTranscribing(video.id);

    try {
      // For YouTube videos, we can use YouTube's auto-generated captions
      // For self-hosted videos, we'll need to use Whisper API
      
      if (video.videoType === 'youtube') {
        // YouTube videos - we'll need to implement caption extraction
        toast({
          title: "Info",
          description: "YouTube transcript extraction coming soon. For now, please upload a manual transcript.",
        });
      } else {
        // Self-hosted videos - use Whisper API via edge function
        toast({
          title: "Processing",
          description: "Downloading and transcribing video... This may take a few minutes.",
        });

        const data: any = await apiClient.post('/api/v1/ai/videos/transcribe', {
          video_url: video.videoUrl,
          video_id: video.id,
          title: video.title
        });

        if (!data?.success || !data?.transcript) {
          // Check if it's a file size error with suggestion
          if (data?.suggestion) {
            toast({
              title: "File Too Large",
              description: data?.error || 'Video file is too large for transcription',
              variant: "destructive"
            });
            // Show suggestion in a follow-up toast
            setTimeout(() => {
              toast({
                title: "Tip",
                description: data?.suggestion,
              });
            }, 100);
            return; // Exit early, don't throw
          }
          throw new Error(data?.error || 'No transcript generated');
        }

        // Save transcript as knowledge document (use shared tenant for all videos)
        const result = await knowledgeService.uploadDocument(
          new File([data?.transcript], `${video.title}_transcript.txt`, { type: 'text/plain' }),
          `${video.title} - Transcript`,
          'sales_training',
          SHARED_TENANT_ID,  // All sales training videos go to shared tenant
          user.id,
          {
            video_id: video.id,
            video_title: video.title,
            requires_citation: true,
            is_global: video.isGlobal,
            word_count: data?.word_count
          }
        );

        if (result.success) {
          toast({
            title: "Success",
            description: `Transcript generated for "${video.title}" (${data?.word_count} words)`,
          });
          await loadVideos();
          onUpdate?.();
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error: any) {
      console.error('Error transcribing video:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to transcribe video. Please check the video URL and try again.",
        variant: "destructive"
      });
    } finally {
      setTranscribing(null);
    }
  };

  const handleToggleVideo = (videoId: string) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId);
    } else {
      newSelected.add(videoId);
    }
    setSelectedVideos(newSelected);
  };

  const handleUploadTranscript = async () => {
    if (!selectedVideoForUpload || !manualTranscript.trim()) return;
    if (!user?.id) return;

    setUploadingTranscript(true);
    
    try {
      // Format the manual transcript
      const formattedTranscript = `
Video Title: ${selectedVideoForUpload.title}
Video ID: ${selectedVideoForUpload.id}
Transcription Date: ${new Date().toISOString()}
Transcription Source: Manual Upload
---

${manualTranscript.trim()}
      `.trim();
      
      // Save transcript as knowledge document
      const result = await knowledgeService.uploadDocument(
        new File([formattedTranscript], `${selectedVideoForUpload.title}_transcript.txt`, { type: 'text/plain' }),
        `${selectedVideoForUpload.title} - Transcript`,
        'sales_training',
        SHARED_TENANT_ID,
        user.id,
        {
          video_id: selectedVideoForUpload.id,
          video_title: selectedVideoForUpload.title,
          requires_citation: true,
          is_global: selectedVideoForUpload.isGlobal,
          word_count: manualTranscript.trim().split(/\s+/).length,
          transcription_method: 'manual'
        }
      );

      if (result.success) {
        toast({
          title: "Success",
          description: `Transcript uploaded for "${selectedVideoForUpload.title}"`,
        });
        await loadVideos();
        onUpdate?.();
        setUploadDialogOpen(false);
        setManualTranscript('');
        setSelectedVideoForUpload(null);
      } else {
        throw new Error(result.error);
      }
    } catch (error: any) {
      console.error('Error uploading transcript:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload transcript",
        variant: "destructive"
      });
    } finally {
      setUploadingTranscript(false);
    }
  };

  const handleSaveSelection = async () => {
    if (!user?.id) return;

    setSaving(true);

    try {
      // Save the selected video IDs to knowledge source configuration via API
      await apiClient.put('/api/v1/ai/config/sources', {
        source_type: 'sales_training',
        is_enabled: true,
        configuration: {
          selected_video_ids: Array.from(selectedVideos),
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }
      });

      toast({
        title: "Success",
        description: "Training video selection saved",
      });
      
      onUpdate?.();
    } catch (error) {
      console.error('Error saving video selection:', error);
      toast({
        title: "Error",
        description: "Failed to save video selection",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredVideos = videos.filter(video =>
    video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const globalVideos = filteredVideos.filter(v => v.isGlobal);
  const tenantVideos = filteredVideos.filter(v => !v.isGlobal);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Sales Training Knowledge Base
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <Quote className="h-4 w-4 text-yellow-600" />
              <h3 className="font-medium text-yellow-900">Citations Required</h3>
            </div>
            <p className="text-yellow-700 text-sm">
              Sales training content requires source citations in AI responses. 
              Videos with transcripts can be included in the knowledge base.
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Input
              placeholder="Search training videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Video className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          </div>

          {/* Video Selection List */}
          <div className="space-y-4">
            {/* Global Videos Section */}
            {globalVideos.length > 0 && (
              <div>
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Company Training Videos (Shared)
                </h3>
                <ScrollArea className="h-64 border rounded-lg p-2">
                  {globalVideos.map((video) => (
                    <Card key={video.id} className="mb-2 p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <button
                            onClick={() => handleToggleVideo(video.id)}
                            disabled={!video.hasTranscript}
                            className={!video.hasTranscript ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          >
                            {selectedVideos.has(video.id) ? (
                              <CheckSquare className="h-5 w-5 text-primary" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                          <div className="flex-1">
                            <h4 className="font-medium">{video.title}</h4>
                            {video.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {video.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              {video.hasTranscript ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  Has Transcript
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600 border-orange-600">
                                  No Transcript
                                </Badge>
                              )}
                              <Badge variant="secondary">
                                {video.videoType === 'youtube' ? 'YouTube' : 'Self-hosted'}
                              </Badge>
                            </div>
                          </div>
                          {!video.hasTranscript && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleTranscribeVideo(video)}
                                disabled={transcribing === video.id}
                              >
                                {transcribing === video.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Mic className="h-4 w-4" />
                                )}
                                Transcribe
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedVideoForUpload(video);
                                  setUploadDialogOpen(true);
                                }}
                              >
                                <FileText className="h-4 w-4" />
                                Upload Text
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </ScrollArea>
              </div>
            )}

            {/* Tenant Videos Section */}
            {tenantVideos.length > 0 && (
              <div>
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Organization Training Videos (Private)
                </h3>
                <ScrollArea className="h-64 border rounded-lg p-2">
                  {tenantVideos.map((video) => (
                    <Card key={video.id} className="mb-2 p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <button
                            onClick={() => handleToggleVideo(video.id)}
                            disabled={!video.hasTranscript}
                            className={!video.hasTranscript ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          >
                            {selectedVideos.has(video.id) ? (
                              <CheckSquare className="h-5 w-5 text-primary" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                          <div className="flex-1">
                            <h4 className="font-medium">{video.title}</h4>
                            {video.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {video.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              {video.hasTranscript ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  Has Transcript
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600 border-orange-600">
                                  No Transcript
                                </Badge>
                              )}
                              <Badge variant="secondary">
                                {video.videoType === 'youtube' ? 'YouTube' : 'Self-hosted'}
                              </Badge>
                            </div>
                          </div>
                          {!video.hasTranscript && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleTranscribeVideo(video)}
                                disabled={transcribing === video.id}
                              >
                                {transcribing === video.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Mic className="h-4 w-4" />
                                )}
                                Transcribe
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedVideoForUpload(video);
                                  setUploadDialogOpen(true);
                                }}
                              >
                                <FileText className="h-4 w-4" />
                                Upload Text
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </ScrollArea>
              </div>
            )}

            {filteredVideos.length === 0 && (
              <Card className="p-8 text-center">
                <Video className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="font-medium mb-2">No Training Videos Found</h3>
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? "No videos match your search"
                    : "No training videos have been uploaded yet"}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Debug: videos.length = {videos.length}, filteredVideos.length = {filteredVideos.length}, globalVideos.length = {globalVideos.length}, tenantVideos.length = {tenantVideos.length}
                </p>
              </Card>
            )}
          </div>

          {/* Save Button */}
          {videos.length > 0 && (
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveSelection}
                disabled={saving}
                className="flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckSquare className="h-4 w-4" />
                    Save Selection
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Citation Format</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  AI responses will include proper citations for sales training content.
                </p>
                <Badge variant="default">Format: [Video Title, Timestamp]</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Scope Management</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Global videos are shared, tenant videos stay private.
                </p>
                <Badge variant="secondary">Automatic Isolation</Badge>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Upload Transcript Panel */}
      <SlidePanel
        isOpen={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        title="Upload Transcript"
        width="lg"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Manually enter the transcript for "{selectedVideoForUpload?.title}"
          </p>

          <div className="space-y-2">
            <Label htmlFor="transcript">Transcript Text</Label>
            <Textarea
              id="transcript"
              placeholder="Enter or paste the transcript here..."
              value={manualTranscript}
              onChange={(e) => setManualTranscript(e.target.value)}
              className="min-h-[300px] resize-y"
            />
            <p className="text-sm text-muted-foreground">
              {manualTranscript.trim() ?
                `Word count: ${manualTranscript.trim().split(/\s+/).length}` :
                'Enter the transcript text above'}
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setManualTranscript('');
                setSelectedVideoForUpload(null);
              }}
              disabled={uploadingTranscript}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadTranscript}
              disabled={!manualTranscript.trim() || uploadingTranscript}
            >
              {uploadingTranscript ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Upload Transcript'
              )}
            </Button>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
};